import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import FloatingContact from "../components/FloatingContact";
import routes from "./config";
import { Styles } from "../styles/styles";

const NotFound = lazy(() => import("../pages/NotFound"));

/**
 * Browsers restore scroll position on history navigation, but a client-side
 * route change is not a navigation as far as the browser is concerned — without
 * this, following a footer link lands the visitor halfway down the new page.
 */
const ScrollToTopOnNavigate = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    if (
      typeof window === "undefined" ||
      !window.matchMedia ||
      !window.matchMedia("(prefers-reduced-motion: no-preference)").matches
    ) {
      return;
    }

    const animated = new WeakSet<Element>();
    const selector =
      "section h1, section h2, section h3, section p, .stat-tile, .gallery-item, .trade-family-row, .explore-row, .channel-tile, tbody tr, figure";

    const triggerMotion = (el: Element, index: number) => {
      if (animated.has(el)) return;
      animated.add(el);
      const htmlEl = el as HTMLElement;
      const delay = Math.min(index * 35, 180);
      htmlEl.style.transform = "translateY(12px)";
      if (typeof htmlEl.animate === "function") {
        const anim = htmlEl.animate(
          [
            { opacity: 0.72, transform: "translateY(12px)" },
            { opacity: 1, transform: "translateY(0px)" },
          ],
          {
            duration: 460,
            delay,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            fill: "forwards",
          }
        );
        anim.onfinish = () => {
          htmlEl.style.transform = "translateY(0px)";
        };
      } else {
        htmlEl.style.transform = "translateY(0px)";
      }
    };

    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((entry, idx) => {
                if (entry.isIntersecting) {
                  triggerMotion(entry.target, idx);
                  observer?.unobserve(entry.target);
                }
              });
            },
            { threshold: 0.08 }
          )
        : null;

    const scan = () => {
      const nodes = document.querySelectorAll(selector);
      nodes.forEach((node) => {
        if (!animated.has(node)) {
          observer?.observe(node);
        }
      });
    };

    const timer1 = window.setTimeout(scan, 60);
    const timer2 = window.setTimeout(scan, 450);

    return () => {
      window.clearTimeout(timer1);
      window.clearTimeout(timer2);
      observer?.disconnect();
    };
  }, [pathname]);

  return null;
};

const Router = () => {
  return (
    <Suspense fallback={null}>
      <Styles />
      <ScrollToTopOnNavigate />
      <Header />
      <Switch>
        {routes.map((routeItem) => {
          return (
            <Route
              key={routeItem.component}
              path={routeItem.path}
              exact={routeItem.exact}
              component={lazy(() => import(`../pages/${routeItem.component}`))}
            />
          );
        })}

        {/* `/home` used to serve the same content as `/`, which split ranking
            signals across two URLs for one page. It now consolidates onto the
            canonical root. */}
        <Redirect exact from="/home" to="/" />

        <Route component={NotFound} />
      </Switch>
      <Footer />
      <FloatingContact />
    </Suspense>
  );
};

export default Router;
